/**
 * Payment Breakdown Mixin
 *
 * Merges org-profile visibility with card/modal payment line items.
 *
 * All API from this file uses the `pbd_` prefix (payment breakdown) so it is obvious
 * in components and in grep — same idea as `rsm_` on refine-search-mobile-mixin.
 *
 * Visibility: `pricingConfiguration.paymentBreakdowns.isPaymentBreakdownsEnabled` only
 * (no manufacturer allow-list).
 *
 * Host contract (provided by the consuming component):
 * - `displayWeeklyPrice` (boolean) — current cadence
 * - `weeklyPrice` / `monthlyPrice` — headline lease price for the cadence,
 *    used for the modal summary ("Estimated weekly/monthly payment")
 * - `pbd_ratebookRow` — ratebook row / vehicle item that contains the four
 *    backend fields named in `ratebookPaymentBreakdownFields.js`
 *    (vehicleFinance / vehicleFinanceWeekly / vehicleRunningCost /
 *    vehicleRunningCostWeekly). Safe to be `null` — values just hide.
 */
import { mapGetters } from 'vuex';
import { getPaymentBreakdownFormattedParts, formatPaymentBreakdownAmount } from '../../utils/calculator/ratebookPaymentBreakdownFields';

export default {
  computed: {
    ...mapGetters({
      orgProfile: 'theme/getOrgProfile'
    }),
    pbd_isPaymentBreakdownsEnabled() {
      return !!this.orgProfile?.pricingConfiguration?.paymentBreakdowns?.isPaymentBreakdownsEnabled;
    },
    pbd_cardTotalLabel() {
      if (typeof this.displayWeeklyPrice !== 'boolean') return '';
      return this.displayWeeklyPrice ? 'Estimated weekly payment' : 'Estimated monthly payment';
    },
    /** Modal summary — same headline lease price the customer sees on the card / header. */
    pbd_cardEstimatedPayment() {
      if (typeof this.displayWeeklyPrice !== 'boolean') return '';
      const value = this.displayWeeklyPrice ? this.weeklyPrice : this.monthlyPrice;
      return formatPaymentBreakdownAmount(value);
    },
    /** Vehicle finance + bundled running costs read from `pbd_ratebookRow`. */
    pbd_cardParts() {
      if (typeof this.displayWeeklyPrice !== 'boolean') return null;
      const parts = getPaymentBreakdownFormattedParts(this.pbd_ratebookRow, this.displayWeeklyPrice);
      if (!parts.finance && !parts.bundled) return null;
      return parts;
    }
  }
};
